import { filter, map } from "lodash";
import { Facet } from "@linkfluence/models/facet";
import { natural } from "@tootools/sort";
import { Card } from "@radarly/models/cards";
import {
  isAICard,
  isClassificationCard,
  isDateMetricCard,
  isDateMetricsCard,
  isDateTermCard,
  isDemographicCard,
  isEmbeddedCard,
  isEmvCard,
  isGeoCard,
  isPinnedAuthorCard,
  isPinnedPostCard,
  isPivotCard,
  isTermCard,
  isTermMetricsCard,
  isTextCard,
} from "@radarly/models/cards/is";
import { FullFacet } from "@radarly/models/facets";
import {
  getComparaisonPeriodFromFragments,
  isComparaisonPeriodFromFragmentsBothPreviousAndLastYearPeriod,
} from "../fragment";
import { CardWithTerm, PivotCard, PostsCard, UsersCard } from "./model";
import { isCardTerm, isPivotCardTerm } from "./term";

export function hasTitleCard(card: Card): boolean {
  return !isAICard(card);
}

export function hasAttributeField(card: Card): card is CardWithTerm {
  return (
    isTermCard(card) ||
    isDateTermCard(card) ||
    isGeoCard(card) ||
    isClassificationCard(card) ||
    isDemographicCard(card) ||
    isDateMetricCard(card) ||
    isTermMetricsCard(card) ||
    isPivotCard(card) ||
    isDateMetricsCard(card)
  );
}

export function hasTwoAttributeFields(card: Card): card is PivotCard {
  return isPivotCard(card);
}

export function hasMetricField(card: Card) {
  return (
    isTermCard(card) ||
    isDateTermCard(card) ||
    isGeoCard(card) ||
    isDateMetricCard(card) ||
    isPivotCard(card) ||
    isClassificationCard(card) ||
    isDemographicCard(card)
  );
}

export function hasManyMetricFields(card: Card) {
  return isDateMetricsCard(card) || isTermMetricsCard(card);
}

export function hasDatavizButton(card: Card) {
  return (
    !isEmbeddedCard(card) &&
    !isTextCard(card) &&
    !isPinnedPostCard(card) &&
    !isPinnedAuthorCard(card) &&
    // @TO-DO: double-check here
    !isAICard(card) &&
    !isEmvCard(card)
  );
}

export function getTimeUnitFromCard(card: Card) {
  if (card.fragments.length === 1) return null;
  return getComparaisonPeriodFromFragments(
    card.fragments[0],
    card.fragments[1]
  );
}
export function isComparisonPeriodFromCardBothPreviousAndLastYearPeriod(
  card: Card
) {
  if (card.fragments.length === 1) return false;
  return isComparaisonPeriodFromFragmentsBothPreviousAndLastYearPeriod(
    card.fragments[0],
    card.fragments[1]
  );
}
export function getComparaisonPeriodFromCard(card: Card) {
  if (card.fragments.length === 1) return null;
  return getComparaisonPeriodFromFragments(
    card.fragments[0],
    card.fragments[1]
  );
}

export const getAvailableFacetsFromCard = (
  card: Card,
  facets: Record<string, FullFacet>
) => {
  return filter(getAvailableFacets(facets), (facet) => {
    if (isPivotCard(card)) {
      return isPivotCardTerm(facet.value as Facet, facets[facet.value]);
    } else {
      return true;
    }
  });
};

export const getAvailableFacets = (facets: Record<string, FullFacet>) => {
  return natural(
    map(
      filter(facets, (facet) => {
        return facet.custom || isCardTerm(facet.id as Facet);
      }),
      (facet) => ({
        value: facet.id,
        label: facet.label,
      })
    ),
    "label"
  );
};

export const TermSelectionMethods = [
  "frequency",
  "significant",
  "emerging",
] as const;
export type TermSelectionMethod = typeof TermSelectionMethods[number];
const TermTypes = [
  "hashtags",
  "mentions",
  "namedEntities",
  "entities",
  "keyphrases",
] as const;
export type TermType = typeof TermTypes[number];

/**
 * Get the composition of a TermFacet to help changing one of its component
 * e.g. Facet.SignificantHashtags is made from "significant" + "hashtags"
 */
const termFetcher: Record<
  TermSelectionMethod,
  Partial<Record<TermType, Facet>>
> = {
  frequency: {
    hashtags: Facet.Hashtags,
    mentions: Facet.Mentions,
    namedEntities: Facet.Entities,
    entities: Facet.ResolvedEntities,
    keyphrases: Facet.Keyphrases,
  },
  significant: {
    hashtags: Facet.SignificantHashtags,
    mentions: Facet.SignificantMentions,
    namedEntities: Facet.SignificantNamedEntities,
    entities: Facet.SignificantEntities,
    keyphrases: Facet.SignificantKeyphrases,
  },
  emerging: {
    hashtags: Facet.EmergingHashtags,
    mentions: Facet.EmergingMentions,
    namedEntities: Facet.EmergingNamedEntities,
    keyphrases: Facet.EmergingKeyphrases,
  },
};

const buildTermFacet = (
  selectionMethod: TermSelectionMethod,
  termType: TermType
): Facet => {
  return termFetcher[selectionMethod]?.[termType];
};

export const termHasSelectionMethods = (term: string) => {
  const [selectionMethod, termType] = getTermSelectionAndType(term);
  return !!buildTermFacet(selectionMethod, termType);
};

/**
 * Retrieve a TermFacet's selection method and type
 * e.g. getTermSelectionAndType("significantHashtags") ==> ["significant", "hashtags"]
 */
export const getTermSelectionAndType = (
  term: string
): [TermSelectionMethod, TermType] => {
  for (const [selectionMethod, termTypeToFacets] of Object.entries(
    termFetcher
  )) {
    for (const [baseType, currentTerm] of Object.entries(termTypeToFacets)) {
      if (currentTerm === term) {
        return [selectionMethod as TermSelectionMethod, baseType as TermType];
      }
    }
  }
  return [null, null];
};

export const changeTermSelectionMethod = (
  term: string,
  targetSelection: TermSelectionMethod = "frequency"
) => {
  const [currentSelection, termType] = getTermSelectionAndType(term);

  if (currentSelection === targetSelection) {
    return term; // No change needed
  }
  return buildTermFacet(targetSelection, termType) || term;
};

export const getPostSort = (card: PostsCard) =>
  card.fragments[0].request.pagination.sort.on;

export const getUserSort = (card: UsersCard) =>
  card.fragments[0].request.aggs.users.sort.on;
